import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * The small markdown the assistant is told to use: paragraphs, bullet and numbered lists, pipe tables, bold, code and
 * links. App paths ("/customers/cus_x") become router links; anything else opens in a new tab. No HTML is ever
 * interpreted, so model output cannot inject markup.
 */
export function Markdown({ text }: { text: string }) {
  const blocks = splitBlocks(text);
  return (
    <div className="space-y-2">
      {blocks.map((b, i) => {
        if (b.kind === "ul") return <ul key={i} className="list-disc pl-4 space-y-0.5">{b.lines.map((l, j) => <li key={j}>{inline(l)}</li>)}</ul>;
        if (b.kind === "ol") return <ol key={i} className="list-decimal pl-4 space-y-0.5">{b.lines.map((l, j) => <li key={j}>{inline(l)}</li>)}</ol>;
        if (b.kind === "table") return <Table key={i} rows={b.lines} />;
        if (b.kind === "heading") return <p key={i} className="font-bold">{inline(b.lines[0]!)}</p>;
        return <p key={i} className="whitespace-pre-wrap">{b.lines.map((l, j) => <Fragment key={j}>{j > 0 ? <br /> : null}{inline(l)}</Fragment>)}</p>;
      })}
    </div>
  );
}

type Block = { kind: "p" | "ul" | "ol" | "table" | "heading"; lines: string[] };

function splitBlocks(text: string): Block[] {
  const out: Block[] = [];
  let cur: Block | null = null;
  const flush = () => { if (cur && cur.lines.length) out.push(cur); cur = null; };
  for (const raw of text.replace(/\r/g, "").split("\n")) {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); continue; }
    const kind: Block["kind"] = /^\s*([-*•]\s+)/.test(line) ? "ul" : /^\s*\d+[.)]\s+/.test(line) ? "ol" : /^\s*\|.*\|\s*$/.test(line) ? "table" : /^#{1,6}\s+/.test(line) ? "heading" : "p";
    const content = kind === "ul" ? line.replace(/^\s*[-*•]\s+/, "") : kind === "ol" ? line.replace(/^\s*\d+[.)]\s+/, "") : kind === "heading" ? line.replace(/^#{1,6}\s+/, "") : line;
    if (kind === "table" && /^\s*\|?\s*:?-{2,}/.test(content.replace(/\|/g, " "))) continue; // separator row
    if (!cur || cur.kind !== kind || kind === "heading") { flush(); cur = { kind, lines: [] }; }
    cur.lines.push(content);
  }
  flush();
  return out;
}

function Table({ rows }: { rows: string[] }) {
  const cells = rows.map((r) => r.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim()));
  const [head, ...body] = cells;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        {head ? <thead><tr>{head.map((c, i) => <th key={i} className="text-left font-bold pr-3 pb-1 border-b border-ink/15">{inline(c)}</th>)}</tr></thead> : null}
        <tbody>{body.map((r, i) => <tr key={i} className="border-b border-ink/5">{r.map((c, j) => <td key={j} className="pr-3 py-1 align-top">{inline(c)}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

function inline(text: string): ReactNode {
  const parts = text.split(INLINE).filter((p) => p !== "");
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("`") && p.endsWith("`")) return <code key={i} className="rounded bg-ink/5 px-1 font-mono text-[0.9em]">{p.slice(1, -1)}</code>;
    const m = p.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (m) {
      const [, label, href] = m;
      if (href!.startsWith("/") && !href!.startsWith("//")) return <Link key={i} to={href!} className="font-semibold text-primary-dark hover:underline">{label} →</Link>;
      if (/^https?:\/\//.test(href!)) return <a key={i} href={href} target="_blank" rel="noopener" className="font-semibold text-primary-dark hover:underline">{label} ↗</a>;
      return <span key={i}>{label}</span>;
    }
    return <Fragment key={i}>{p}</Fragment>;
  });
}
